import { Trans, useLingui } from '@lingui/react/macro'
import {
  EyeNoneIcon,
  EyeOpenIcon,
  InfoCircledIcon,
} from '@radix-ui/react-icons'
import CantileverButton from 'components/CantileverButton'
import { useFimSubjectsByDatapoints } from 'features/fim'
import { useScatterplotContext } from 'features/timeSeries'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { PenSubjectLinksSection } from 'features/timeSeries/components/PenSubjectLinksSection'
import { PenTransformsPopover } from 'features/timeSeries/components/PenTransformControls'
import { TruncationTooltip } from 'features/timeSeries/components/TruncationTooltip'
import { CHART_COLORS } from 'features/timeSeries/config/constants'
import { getSensorColor } from 'features/timeSeries/config/timeSeriesColors'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useWindowsToolView } from 'features/timeSeries/experiment/ExperimentLabContext'
import { usePenVisibilityActions } from 'features/timeSeries/hooks/usePenVisibilityActions'
import { useSentinelOverlayMap } from 'features/timeSeries/hooks/useSentinelOverlayMap'
import {
  bandEdgesFor,
  residualFor,
  SENTINEL_OUT_OF_BAND_COLOR,
  sentinelReadingFor,
  SIGMA_WARN,
} from 'features/timeSeries/hooks/useSentinelScores'
import { useTimeSeriesSensors } from 'features/timeSeries/hooks/useTimeSeriesSensors'
import { formatCurrentValue } from 'features/timeSeries/utils/scatterPlotTableHelpers'
import { digitalStateName } from 'features/timeSeries/utils/steppedPens'
import {
  Bell,
  BellOff,
  ChevronRight,
  SlidersHorizontal,
  Waypoints,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { EnhancedSensorEntry, TimeSeriesDerivedValueEntryJSON } from 'types'

const DRAWER_WIDTH = 220
const SKELETON_COUNT = 5

const DrawerContainer = styled.div<{ $fluid: boolean }>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  ${({ $fluid }) =>
    $fluid
      ? 'width: 100%; min-width: 0; height: 100%;'
      : `width: ${DRAWER_WIDTH}px; min-width: ${DRAWER_WIDTH}px; background: ${CHART_COLORS.BACKGROUND}; padding-top: 3rem;`}
`

const DrawerActionsContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  z-index: 10;
  display: flex;
  gap: 0.25rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 0.25rem;
  padding: 0.125rem;
`

const PenList = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
`

const PenRow = styled.div<{ $dimmed: boolean; $highlighted: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.1875rem 0.25rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.4 : 1)};
  font-weight: ${({ $highlighted }) => ($highlighted ? 600 : 400)};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${({ theme }) => theme.forecast.hoverGhostButton};
    opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
  }
`

const ColorDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

const TransformButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 0.125rem;
  padding: 0.0625rem;
  border: none;
  border-radius: 3px;
  background: none;
  cursor: pointer;
  line-height: 0;
  color: ${({ $active, theme }) =>
    $active ? theme.forecast.activeText : theme.forecast.secondaryText};
  opacity: ${({ $active }) => ($active ? 1 : 0)};

  ${PenRow}:hover & {
    opacity: 1;
  }

  &:focus-visible {
    opacity: 1;
    outline: 1px solid ${({ theme }) => theme.forecast.activeText};
  }

  &:hover {
    background: ${({ theme }) => theme.forecast.cardDividerBackground};
  }
`

const SubjectDisclosureButton = styled.button<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: 0.125rem;
  padding: 0.0625rem;
  border: none;
  border-radius: 3px;
  background: none;
  cursor: pointer;
  line-height: 0;
  color: ${({ theme }) => theme.forecast.secondaryText};

  svg {
    transition: transform 120ms ease;
    transform: rotate(${({ $expanded }) => ($expanded ? 90 : 0)}deg);
  }

  &:focus-visible {
    outline: 1px solid ${({ theme }) => theme.forecast.activeText};
  }

  &:hover {
    background: ${({ theme }) => theme.forecast.cardDividerBackground};
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const PenInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const PenMainLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  min-width: 0;
`

const TagName = styled.span`
  font-size: 0.65rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
  min-width: 0;
`

const DescriptionViewport = styled.div`
  min-width: 0;
  overflow: hidden;
`

const scrollDescription = keyframes`
  0%,
  15% {
    transform: translateX(0);
  }

  85%,
  100% {
    transform: translateX(calc(var(--description-scroll-distance, 0px) * -1));
  }
`

const DescriptionTrack = styled.div<{ $canScroll: boolean }>`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
  line-height: 1.2;
  display: inline-block;
  width: max-content;
  max-width: 100%;

  ${({ $canScroll }) =>
    $canScroll &&
    css`
      ${DescriptionViewport}:hover & {
        animation: ${scrollDescription} var(--description-scroll-duration, 6s)
          ease-in-out infinite alternate;
      }
    `}
`

const CurrentValue = styled.span`
  font-size: 0.6rem;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  color: ${({ theme }) => theme.forecast.activeText};
  white-space: nowrap;
  flex-shrink: 0;
  margin-left: auto;
`

const SigmaBadge = styled.span<{ $outOfBand: boolean }>`
  color: ${({ theme, $outOfBand }) =>
    $outOfBand ? SENTINEL_OUT_OF_BAND_COLOR : theme.forecast.secondaryText};
  font-size: 0.6rem;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
`

const RightCluster = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 0.375rem;
  margin-left: auto;
  flex-shrink: 0;
`

const TargetTag = styled.span`
  flex-shrink: 0;
  padding: 0 3px;
  border: 1px solid currentColor;
  border-radius: 2px;
  font-size: 0.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  line-height: 1.5;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const SentinelIcon = styled(Waypoints)`
  width: 13px;
  height: 13px;
  color: ${SENTINEL_OUT_OF_BAND_COLOR};
  flex-shrink: 0;
`

const SentinelSubLabel = styled.span`
  font-size: 0.55rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: nowrap;
  line-height: 1.2;
`

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

const SkeletonBar = styled.div<{ $width: string }>`
  height: 0.55rem;
  width: ${({ $width }) => $width};
  border-radius: 2px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.8s ease-in-out infinite;
`

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.25rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

const SkeletonDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.06);
`

const SkeletonLabel = styled.span`
  font-size: 0.6rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  padding: 0.375rem 0.25rem;
  opacity: 0.6;
`

interface HoverScrollableDescriptionProps {
  text: string
}

const HoverScrollableDescription: React.FC<HoverScrollableDescriptionProps> = ({
  text,
}) => {
  const [scrollDistance, setScrollDistance] = useState(0)
  const [scrollDuration, setScrollDuration] = useState(0)

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const viewport = event.currentTarget
      const track = viewport.firstElementChild

      if (!(track instanceof HTMLElement)) {
        setScrollDistance(0)
        setScrollDuration(0)
        return
      }

      const overflowAmount = Math.ceil(track.scrollWidth - viewport.clientWidth)

      if (overflowAmount <= 0) {
        setScrollDistance(0)
        setScrollDuration(0)
        return
      }

      setScrollDistance(overflowAmount)
      setScrollDuration(Math.max(4, overflowAmount / 18))
    },
    []
  )

  return (
    <DescriptionViewport onMouseEnter={handleMouseEnter}>
      <DescriptionTrack
        $canScroll={scrollDistance > 0}
        style={
          {
            '--description-scroll-distance': `${scrollDistance}px`,
            '--description-scroll-duration': `${scrollDuration}s`,
          } as React.CSSProperties
        }>
        {text}
      </DescriptionTrack>
    </DescriptionViewport>
  )
}

/**
 * Resolves the display unit for a sensor or derived value.
 */
const resolveUnit = (
  id: string,
  sensorRanges: Record<string, { min?: number; max?: number; unit: string }>,
  derivedValues: TimeSeriesDerivedValueEntryJSON[],
  enhancedSensorEntries: EnhancedSensorEntry[]
): string => {
  if (id.startsWith('derived_')) {
    const dv = derivedValues.find((d) => d.id === id)
    if (dv?.data.unit && dv.data.unit !== 'No Unit') return dv.data.unit
    return ''
  }

  const range = sensorRanges[id]
  if (range?.unit && range.unit !== 'No Unit') return range.unit

  const sensor = enhancedSensorEntries.find((e) => e.id === id)
  if (sensor?.data?.unit && sensor.data.unit !== 'No Unit') {
    return sensor.data.unit as string
  }

  return ''
}

interface PenDrawerProps {
  showDescriptions?: boolean
  hideHeaderActions?: boolean
  /** Fill the parent (rail pane) instead of the fixed right-drawer column. */
  fluid?: boolean
  /** Transforms belong to experiment surfaces; the workbench hides them. */
  hideTransforms?: boolean
}

export const PenDrawer: React.FC<PenDrawerProps> = ({
  showDescriptions: controlledShowDescriptions,
  hideHeaderActions = false,
  fluid = false,
  hideTransforms = false,
}) => {
  const { t } = useLingui()

  const [localShowDescriptions, setShowDescriptions] = useState(true)
  const showDescriptions = controlledShowDescriptions ?? localShowDescriptions

  const {
    siteID,
    selectedSensors,
    sensorEntries,
    derivedValues,
    sensorRanges,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    hideAllAlerts,
    setHideAllAlerts,
    customSensorColors,
    highlightedSensorId,
    setHighlightedSensorId,
    localThresholdOverrides,
    isLoading,
    isInitializing,
    pendingSensorIds,
    activeSentinels,
    sentinelVisibilityConfig,
    setSentinelVisibilityConfig,
    thresholdFilters,
    smoothingConfigs,
    resampleConfigs,
  } = useScatterplotContext()

  const { cursorData } = useCursorData()
  // includeHidden: a hidden sentinel's drawer row is the way to re-show it.
  const overlaysByTarget = useSentinelOverlayMap({ includeHidden: true })
  const { config: labConfig, engaged: labEngaged } = useWindowsToolView()
  const labTarget = labEngaged ? (labConfig?.target ?? '') : ''

  const { enhancedSensorEntries } = useTimeSeriesSensors({
    sensorEntries,
    selectedSensors,
    localThresholdOverrides,
  })

  const {
    allItems,
    areAllHidden: areAllItemsHidden,
    setAllVisibility: handleSetAllVisibility,
  } = usePenVisibilityActions()

  const handleToggleVisibility = useCallback(
    (itemId: string) => {
      setSensorVisibilityConfig((prev) => ({
        ...prev,
        [itemId]: !(prev[itemId] ?? true),
      }))
    },
    [setSensorVisibilityConfig]
  )

  const handleToggleSentinelVisibility = useCallback(
    (entryId: string) => {
      setSentinelVisibilityConfig((prev) => ({
        ...prev,
        [entryId]: !(prev[entryId] ?? true),
      }))
    },
    [setSentinelVisibilityConfig]
  )

  const [expandedSubjectLinkIds, setExpandedSubjectLinkIds] = useState<
    Set<string>
  >(new Set())

  const toggleSubjectLinksExpanded = useCallback((itemId: string) => {
    setExpandedSubjectLinkIds((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }, [])

  const sentinelEntryIdByTarget = useMemo(
    () =>
      new Map(
        activeSentinels.map((sentinel) => [sentinel.target, sentinel.entryId])
      ),
    [activeSentinels]
  )

  // The context highlight is shared with other surfaces (manual-alert overlay,
  // chart legend, ruler panel, ...). The drawer may only CLEAR a highlight it
  // set itself via its own mouseenter, or closing the drawer (exclusive panels
  // unmount it) would kill a highlight the alert overlay asserted once and
  // never re-asserts. This ref remembers the value this drawer set; it is
  // cleared on mouseleave, so anything else in the context is not ours.
  const ownedHighlightRef = useRef<string | null>(null)

  // A hovered row can unmount without firing mouseleave (pen deleted, drawer
  // closed), leaving the highlight stuck and every other pen dimmed. Only the
  // drawer's own highlight is cleared, and only once `allItems` has content --
  // during init `selectedSensors` is transiently empty, and an empty list must
  // not clear a highlight some other surface owns.
  useEffect(() => {
    if (
      allItems.length > 0 &&
      highlightedSensorId !== null &&
      highlightedSensorId === ownedHighlightRef.current &&
      !allItems.some((item) => item.id === highlightedSensorId)
    ) {
      ownedHighlightRef.current = null
      setHighlightedSensorId(null)
    }
  }, [allItems, highlightedSensorId, setHighlightedSensorId])

  useEffect(() => {
    return () => {
      const owned = ownedHighlightRef.current
      if (owned === null) return
      // Functional update: null the highlight only if it still is the one this
      // drawer set -- another surface may have replaced it since.
      setHighlightedSensorId((current) => (current === owned ? null : current))
    }
  }, [setHighlightedSensorId])

  const handleMouseEnter = useCallback(
    (itemId: string) => {
      ownedHighlightRef.current = itemId
      setHighlightedSensorId(itemId)
    },
    [setHighlightedSensorId]
  )

  const handleMouseLeave = useCallback(() => {
    const owned = ownedHighlightRef.current
    ownedHighlightRef.current = null
    if (owned === null) return
    setHighlightedSensorId((current) => (current === owned ? null : current))
  }, [setHighlightedSensorId])

  const datapointIds = useMemo(
    () =>
      allItems
        .map((item) => item.id)
        .filter((id) => !id.startsWith('derived_')),
    [allItems]
  )
  const { byDatapoint, subjectsByUid } = useFimSubjectsByDatapoints(
    siteID,
    datapointIds
  )

  if (allItems.length === 0) {
    if (!isLoading && !isInitializing) return null

    const skeletonCount = pendingSensorIds?.length || SKELETON_COUNT
    const defaultWidths = ['72%', '58%', '80%', '50%', '66%']
    return (
      <DrawerContainer $fluid={fluid}>
        <SkeletonLabel>
          <Trans>Loading pens…</Trans>
        </SkeletonLabel>
        <PenList>
          {Array.from({ length: skeletonCount }, (_, i) => (
            <SkeletonRow key={i}>
              <SkeletonDot />
              <SkeletonBar
                $width={defaultWidths[i % defaultWidths.length]}
                style={{ animationDelay: `${i * 120}ms` }}
              />
            </SkeletonRow>
          ))}
        </PenList>
      </DrawerContainer>
    )
  }

  return (
    <DrawerContainer $fluid={fluid}>
      {!hideHeaderActions && (
        <DrawerActionsContainer>
          <ChartTooltip
            label={areAllItemsHidden ? t`Show all pens` : t`Hide all pens`}
            side='bottom'>
            <CantileverButton
              iconOnly
              ghost
              size='small'
              aria-label={
                areAllItemsHidden ? t`Show all pens` : t`Hide all pens`
              }
              onClick={() => handleSetAllVisibility(areAllItemsHidden)}>
              {areAllItemsHidden ? <EyeNoneIcon /> : <EyeOpenIcon />}
            </CantileverButton>
          </ChartTooltip>
          {controlledShowDescriptions === undefined && (
            <ChartTooltip
              label={
                showDescriptions ? t`Hide descriptions` : t`Show descriptions`
              }
              side='bottom'>
              <CantileverButton
                iconOnly
                ghost
                size='small'
                aria-label={
                  showDescriptions ? t`Hide descriptions` : t`Show descriptions`
                }
                onClick={() => setShowDescriptions((current) => !current)}>
                <InfoCircledIcon />
              </CantileverButton>
            </ChartTooltip>
          )}
          <ChartTooltip
            label={hideAllAlerts ? t`Show all alerts` : t`Hide all alerts`}
            side='bottom'>
            <CantileverButton
              iconOnly
              ghost
              size='small'
              aria-label={
                hideAllAlerts ? t`Show all alerts` : t`Hide all alerts`
              }
              onClick={() => setHideAllAlerts((current) => !current)}>
              {hideAllAlerts ? <BellOff size={15} /> : <Bell size={15} />}
            </CantileverButton>
          </ChartTooltip>
        </DrawerActionsContainer>
      )}
      <PenList>
        {(labTarget
          ? [...allItems.entries()].sort(
              ([, a], [, b]) =>
                Number(b.id === labTarget) - Number(a.id === labTarget)
            )
          : [...allItems.entries()]
        ).map(([index, item]) => {
          const color = customSensorColors[item.id] || getSensorColor(index)
          const isHidden = sensorVisibilityConfig[item.id] === false
          const isHighlighted = highlightedSensorId === item.id
          const unit = resolveUnit(
            item.id,
            sensorRanges,
            derivedValues,
            enhancedSensorEntries
          )
          const cursor = cursorData[item.id]
          const sensorData = enhancedSensorEntries.find(
            (e) => e.id === item.id
          )?.data
          const [currentVal] = formatCurrentValue(
            cursor?.yValue,
            unit,
            digitalStateName(sensorData, cursor?.yValue)
          )
          const description = sensorData?.description ?? ''
          const overlay = overlaysByTarget.get(item.id)
          const reading = sentinelReadingFor(overlay, cursor?.xValue)
          const [predictedVal] = reading
            ? formatCurrentValue(reading.predicted, unit)
            : ['']

          const residual = residualFor(overlay, cursor?.xValue)
          const [residualVal] =
            residual === null ? [''] : formatCurrentValue(residual, unit)
          const bandEdges = bandEdgesFor(overlay, cursor?.xValue)
          const bandLabel = bandEdges
            ? `${formatCurrentValue(bandEdges.lower, unit)[0]} – ${
                formatCurrentValue(bandEdges.upper, unit)[0]
              }`
            : ''

          const tooltipLabel = description ? (
            <>
              {item.name}
              <br />
              {description}
            </>
          ) : (
            item.name
          )

          const sentinelEntryId = sentinelEntryIdByTarget.get(item.id)
          const isSentinelHidden =
            sentinelEntryId !== undefined &&
            sentinelVisibilityConfig[sentinelEntryId] === false

          const sentinelRow = overlay && reading && (
            <PenRow
              $dimmed={sentinelEntryId ? isSentinelHidden : false}
              $highlighted={false}
              onClick={
                sentinelEntryId
                  ? () => handleToggleSentinelVisibility(sentinelEntryId)
                  : undefined
              }>
              <SentinelIcon />
              <PenInfo>
                <PenMainLine>
                  <TagName>{overlay.sentinelName}</TagName>
                  <CurrentValue>{predictedVal}</CurrentValue>
                </PenMainLine>
                <PenMainLine>
                  <SentinelSubLabel>predicted</SentinelSubLabel>
                  {residualVal && (
                    <SentinelSubLabel>resid {residualVal}</SentinelSubLabel>
                  )}
                  {bandLabel && (
                    <SentinelSubLabel>band {bandLabel}</SentinelSubLabel>
                  )}
                </PenMainLine>
              </PenInfo>
            </PenRow>
          )

          const hasTransform =
            (thresholdFilters[item.id]?.enabled ?? false) ||
            (smoothingConfigs[item.id]?.enabled ?? false) ||
            (resampleConfigs[item.id]?.enabled ?? false)

          const linkedSubjects = (byDatapoint.get(item.id) ?? [])
            .map((uid) => subjectsByUid.get(uid))
            .filter((subject): subject is NonNullable<typeof subject> =>
              Boolean(subject)
            )

          const penRow = (
            <PenRow
              $dimmed={isHidden}
              $highlighted={isHighlighted}
              onClick={() => handleToggleVisibility(item.id)}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}>
              <ColorDot $color={color} />
              <PenInfo>
                <PenMainLine>
                  <TagName>
                    {item.name}
                    {unit && ` (${unit})`}
                  </TagName>
                  {item.id === labTarget && <TargetTag>target</TargetTag>}
                  <RightCluster>
                    {reading && (
                      <SigmaBadge
                        $outOfBand={Math.abs(reading.sigma) >= SIGMA_WARN}>
                        {reading.sigma > 0 ? '▲' : '▼'}
                        {Math.abs(reading.sigma).toFixed(1)}σ
                      </SigmaBadge>
                    )}
                    <CurrentValue>{currentVal}</CurrentValue>
                  </RightCluster>
                </PenMainLine>
                {showDescriptions && description && (
                  <PenMainLine>
                    <HoverScrollableDescription text={description} />
                  </PenMainLine>
                )}
              </PenInfo>
              {linkedSubjects.length > 0 && (
                <SubjectDisclosureButton
                  type='button'
                  $expanded={expandedSubjectLinkIds.has(item.id)}
                  aria-label={t`Linked subjects for ${{ value769_55: item.name }}`}
                  aria-expanded={expandedSubjectLinkIds.has(item.id)}
                  // the row click toggles visibility
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleSubjectLinksExpanded(item.id)
                  }}>
                  <ChevronRight size={11} />
                </SubjectDisclosureButton>
              )}
              {!hideTransforms && (
                <PenTransformsPopover
                  sensorId={item.id}
                  trigger={
                    <TransformButton
                      $active={hasTransform}
                      aria-label={t`Transforms for ${{ value785_54: item.name }}`}
                      // the row click toggles visibility
                      onClick={(event) => event.stopPropagation()}>
                      <SlidersHorizontal size={11} />
                    </TransformButton>
                  }
                />
              )}
            </PenRow>
          )

          // Tooltip rule: no tooltip when the full text is already visible.
          // With descriptions hidden, a pen's description is NOT visible even
          // when its name fits, so hovering must always surface it. Only when
          // the row shows everything (descriptions on, or no description) is
          // the tooltip gated on actual truncation.
          return (
            <React.Fragment key={item.id}>
              {sentinelRow}
              {!showDescriptions && description ? (
                <ChartTooltip
                  label={tooltipLabel}
                  side='left'
                  style={{ display: 'block' }}>
                  {penRow}
                </ChartTooltip>
              ) : (
                <TruncationTooltip
                  label={tooltipLabel}
                  side='left'
                  style={{ display: 'block' }}>
                  {penRow}
                </TruncationTooltip>
              )}
              {linkedSubjects.length > 0 &&
                expandedSubjectLinkIds.has(item.id) && (
                  <PenSubjectLinksSection
                    siteID={siteID}
                    subjects={linkedSubjects}
                  />
                )}
            </React.Fragment>
          )
        })}
      </PenList>
    </DrawerContainer>
  )
}
